/**
 * Cobble-road auto-connect — the road counterpart of fence-connect.ts.
 *
 * Not the same autotiler: a fence is a LINE (its 16 cases are runs, corners, tees
 * and end caps) while a road is an AREA, so the pack ships an area blob instead —
 * a 3x3 border ring plus four concave corners. Feeding road neighbours through
 * fenceKey would ask for pieces (`_x`, `_end_n`, …) the road sheets do not have,
 * which is why this is a second table rather than a reuse of that one.
 *
 * The pieces the sheet DOESN'T have are the point of the fallback: a 1-tile-wide
 * run needs a north AND a south verge on the same tile, and no such cell exists.
 * Those tiles draw the plain centre — flat cobble edge-to-edge, exactly what
 * shipped before — so laying a lane still reads as one continuous surface and
 * only a paved AREA gains its dirt shoulder.
 *
 * Pure and DOM-free so it is unit-testable; the renderer only appends the
 * returned suffix to the kind and loads `sprites/buildings/<kind><suffix>.png`
 * (baked by scripts/build-decor-sprites.py, same suffix table).
 */

/** Road presence on the 4 sides and 4 diagonals of a tile. */
export interface RoadNeighbours {
  n: boolean; s: boolean; e: boolean; w: boolean;
  ne: boolean; nw: boolean; se: boolean; sw: boolean;
}

/** Which kinds pave an area and therefore autotile. Dirt paths are single tiles. */
export function isRoadKind(kind: string): boolean {
  return kind === 'road' || kind === 'road_sand';
}

/**
 * The sprite suffix for a road tile with these neighbours.
 *
 * `''` is the centre (fully paved). Outer suffixes name the side(s) the pavement
 * STOPS on: `_n` is the north edge, `_nw` the north-west corner. Inner suffixes
 * name the diagonal the dirt pokes through on an otherwise fully surrounded tile.
 */
export function roadKey({ n, s, e, w, ne, nw, se, sw }: RoadNeighbours): string {
  if (n && s && e && w) {
    // Fully surrounded: the only thing left to show is a concave corner, and the
    // sheet has ONE piece per diagonal. With more than one diagonal missing there
    // is no combined cell, and picking the first would drop a dirt square into the
    // middle of the pavement — which is exactly what a plus-shaped CROSSROADS is
    // (four sides paved, all four diagonals empty). Those stay flat centre tiles.
    const gaps = Number(!nw) + Number(!ne) + Number(!sw) + Number(!se);
    if (gaps !== 1) return '';
    if (!nw) return '_inw';
    if (!ne) return '_ine';
    if (!sw) return '_isw';
    if (!se) return '_ise';
    return '';
  }
  if (!n && !w && s && e) return '_nw';
  if (!n && !e && s && w) return '_ne';
  if (!s && !w && n && e) return '_sw';
  if (!s && !e && n && w) return '_se';
  if (!n && s && e && w) return '_n';
  if (!s && n && e && w) return '_s';
  if (!w && n && s && e) return '_w';
  if (!e && n && s && w) return '_e';
  return '';   // ends, lanes and lone tiles — no verge piece exists, stay flat
}

/**
 * Resolve the sprite for a road at (tx, ty) from what stands around it.
 *
 * Only the SAME paving connects: a sandstone road meeting a cobble one keeps its
 * own verge, which is how two different surfaces meeting actually looks.
 */
export function roadSprite(
  kind: string,
  tx: number,
  ty: number,
  at: (tx: number, ty: number) => string | null,
): string {
  const joins = (x: number, y: number) => at(x, y) === kind;
  return kind + roadKey({
    n: joins(tx, ty - 1), s: joins(tx, ty + 1), e: joins(tx + 1, ty), w: joins(tx - 1, ty),
    nw: joins(tx - 1, ty - 1), ne: joins(tx + 1, ty - 1),
    sw: joins(tx - 1, ty + 1), se: joins(tx + 1, ty + 1),
  });
}
